import React, { useState, useEffect } from 'react';
import maestroApiService from '../services/maestroApiService';
import { track } from '../services/analyticsEvents';
import consoleLogger from '../utils/consoleLogger';

const ProfileInput = ({ onSubmitUrl, onBack, onEditPrevious }) => {
  const [url, setUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [welcomeBack, setWelcomeBack] = useState(false);
  const [returningUser, setReturningUser] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState(null);
  // Context strip: shown ONLY on first visit to /optimize for users arriving
  // from off-site (direct/external referrer). Addresses the 73% bounce rate
  // observed for direct /optimize landings vs 15% via internal navigation.
  const [showContextStrip, setShowContextStrip] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem('lpo_seen_optimize_intro');
      if (seen) return;
      const ref = document.referrer || '';
      const cameFromOurSite = ref && ref.includes('optimizer.growleads.io');
      // Show only when external/direct entry (no referrer OR off-site referrer)
      if (!cameFromOurSite) setShowContextStrip(true);
    } catch (_) { /* localStorage may be disabled */ }
  }, []);
  const dismissContextStrip = () => {
    setShowContextStrip(false);
    try { localStorage.setItem('lpo_seen_optimize_intro', '1'); } catch (_) {}
  };

  const validateLinkedInUrl = (inputUrl) => {
    const linkedinPattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    return linkedinPattern.test(inputUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateLinkedInUrl(url)) {
      alert('Please enter a valid LinkedIn profile URL (e.g., https://linkedin.com/in/username)');
      return;
    }
    track.auditStarted(url);
    // Tie every subsequent console log in this session to this profile so
    // the streamed logs in MongoDB can be filtered by profileUrl later.
    consoleLogger.setProfileContext({ profileUrl: url });
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.NODE_ENV === 'production' ? '/api/profile-checker' : 'http://localhost:3001/api/profile-checker';
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const data = await res.json();

      const proceed = () => {
        // Mark as new user for this flow
        try { sessionStorage.setItem('isReturningUser', 'false'); } catch (_) {}
        // Navigate to credit confirmation modal - scraping will start AFTER confirmation
        onSubmitUrl(url);
        // NOTE: Scraping is now handled by App.js after credit confirmation
      };

      if (data && data.success && data.exists) {
        // Returning user: stay on the same page; no auto navigation or scraping
        setWelcomeBack(true);
        setReturningUser(true);
        setExistingRecordId(data.recordId || null);
        try { sessionStorage.setItem('isReturningUser', 'true'); } catch (_) {}
        setIsSubmitting(false);
      } else {
        // New user or checker failed: proceed immediately
        proceed();
      }
    } catch (err) {
      console.error('Profile checker failed:', err);
      // Fallback: proceed normally - scraping handled after credit confirmation
      try { sessionStorage.setItem('isReturningUser', 'false'); } catch (_) {}
      onSubmitUrl(url);
    }
  };

  return (
    <section
      className="section"
      style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 0'
      }}
    >
      <div className="container">

        {/* Context strip: first-visit-only banner for direct/external entries.
            Pre-fix bounce rate on direct /optimize landings was 73%. */}
        {showContextStrip && (
          <div style={{
            maxWidth: 900,
            margin: '0 auto 20px auto',
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%)',
            border: '1px solid #bae6fd',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          }}>
            <div style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#0ea5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 700,
              fontSize: 18,
            }}>👋</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14, marginBottom: 2 }}>
                New here? Here's what happens next.
              </div>
              <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.45 }}>
                Paste your LinkedIn URL → answer a short questionnaire → get a free 50-point AI audit with copy-paste rewrites in about 2 minutes. No signup needed yet.
              </div>
            </div>
            <button
              type="button"
              onClick={dismissContextStrip}
              aria-label="Dismiss intro"
              style={{
                flexShrink: 0,
                background: 'transparent',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: 20,
                padding: '4px 8px',
                lineHeight: 1,
              }}
            >×</button>
          </div>
        )}

        {/* Title and subtitle */}
        <div style={{ maxWidth: 900, margin: '0 auto 24px auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', color: '#1e293b', marginBottom: 8 }}>Enter Your LinkedIn Profile URL</h2>
          <p style={{ color: '#64748b' }}>We'll analyze your profile and provide personalised optimisation recommendations</p>
        </div>

        {/* Content grid - responsive 2 columns on desktop, single column on mobile */}
        <div
          className="grid grid-2 profile-input-grid"
          style={{
            alignItems: 'stretch',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
            maxWidth: 1100,
            margin: '0 auto'
          }}
        >
          {/* Left card - input */}
          <div className="card" style={{ padding: 24 }}>
            <div className="card-header" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span role="img" aria-label="search">🔎</span> Profile URL
              </div>
            </div>

            <p className="card-content" style={{ marginBottom: 12 }}>Paste your LinkedIn profile URL below to get started:</p>

            <form onSubmit={handleSubmit}>
              <input
                type="url"
                className="form-input"
                placeholder="https://linkedin.com/in/yourprofile"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
              {welcomeBack && (
                <div className="alert" style={{
                  marginTop: 10,
                  padding: '12px 14px',
                  borderRadius: 8,
                  background: '#ecfdf5',
                  color: '#065f46',
                  border: '1px solid #a7f3d0',
                  fontWeight: 500
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Welcome back!</div>
                  <div style={{ fontSize: 14, marginBottom: 10 }}>
                    We found your profile in our records. Choose how you want to continue:
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={isLoadingEdit}
                      onClick={() => {
                        // Go to questionnaire with prefilled answers (if any)
                        setIsLoadingEdit(true);
                        if (typeof onEditPrevious === 'function') {
                          try { sessionStorage.setItem('isReturningUser', 'true'); } catch (_) {}
                          onEditPrevious(url);
                        } else {
                          onSubmitUrl(url);
                        }
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      {isLoadingEdit && (
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                      )}
                      {isLoadingEdit ? 'Loading...' : 'Edit previous analysis'}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting || returningUser}>
                  {isSubmitting ? 'Continuing…' : 'Continue'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={onBack}>Back</button>
              </div>
            </form>
          </div>

          {/* Right card - help */}
          <div className="card" style={{ padding: 24 }}>
            <div className="card-header" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span role="img" aria-label="info">💡</span> How to Find Your LinkedIn URL
              </div>
            </div>

            <ol style={{ color: '#64748b', lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Visit your LinkedIn profile</li>
              <li>Copy the URL from your browser's address bar</li>
              <li>It should look like: linkedin.com/in/yourname</li>
            </ol>

            <a
              className="btn btn-secondary"
              href="https://www.linkedin.com/" target="_blank" rel="noreferrer"
              style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
            >
              Redirect to LinkedIn
            </a>
          </div>
        </div>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
};

export default ProfileInput;
